import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";
import { CreateMediaUploadDocument } from "#app/lib/data";
import { useAuth } from "#app/lib/auth-context";
import {
  mediaIdsFromHtml,
  registerMediaObjectUrl,
  resolvePhotoHtml,
  resolveMediaObjectUrl,
  resolveMediaPresignedUrl,
  type PhotoVariant,
} from "#app/lib/media-content";
import {
  FileTooLargeError,
  processMediaForUpload,
  UnsupportedImageError,
} from "#app/lib/media-process";
import { uploadReservedMedia } from "#app/lib/media-upload";
import type { Omittable } from "@salestrak/ui";

/**
 * Uploads a picked image as permission-gated media (notes, photos-type
 * custom fields). The file is first processed client-side (downscaled,
 * re-encoded, metadata stripped, thumbnail generated — see photo-process),
 * then: reserve + presigned PUTs + direct S3 upload of both variants.
 * Returns the photo id and a local object URL for immediate preview
 * (registered with the photo-content cache so HTML restore and later
 * renders reuse it).
 *
 * Optional callbacks surface upload UI state: `onPreview` fires with the local
 * object URL as soon as the file is processed (before the byte PUT) so callers
 * can render an optimistic tile; `onProgress` reports the display-byte PUT as a
 * 0–100 percentage. `signal` cancels an in-flight upload — a cancelled upload
 * resolves to null WITHOUT a toast (the orphaned row is reaped server-side).
 */
export type NotePhotoUploadCallbacks = Omittable<{
  onPreview?: (objectUrl: string) => void;
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
}>;

/** A committed note media upload. `objectUrl` is the composer preview (image
 * display blob, or PDF/video thumb), null for documents. `contentType` drives
 * how the committed card renders; `fileName` labels documents/videos. */
export interface UploadedMedia {
  photoId: string;
  objectUrl: string | null;
  contentType: string;
  fileName: string | null;
}

export function useNoteMediaUpload() {
  const [createMediaUpload] = useMutation(CreateMediaUploadDocument);

  const uploadNotePhoto = useCallback(
    async (
      file: File,
      callbacks?: NotePhotoUploadCallbacks,
    ): Promise<UploadedMedia | null> => {
      const signal = callbacks?.signal;
      const isImage = file.type.startsWith("image/");
      // Created early (pre-PUT) for the optimistic preview tile; revoked on any
      // failure path so an abandoned upload doesn't leak the blob URL. On
      // success it's registered + returned, so the attachment reuses this exact
      // URL rather than minting a second one.
      let objectUrl: string | null = null;
      try {
        const processed = await processMediaForUpload(file).catch((err) => {
          // Both carry a user-facing reason (unsupported format / too large) —
          // toast it and drop the file; anything else is a real error.
          if (
            err instanceof UnsupportedImageError ||
            err instanceof FileTooLargeError
          ) {
            toastManager.add({ title: err.message, type: "error" });
            return null;
          }
          throw err;
        });
        if (!processed) return null;
        if (signal?.aborted) return null;

        objectUrl = URL.createObjectURL(processed.blob);
        // Thumb preview (PDF page 1 / video poster) for non-images — the raw
        // bytes aren't an <Image> source. Images preview the display blob.
        const thumbObjectUrl = processed.thumb
          ? URL.createObjectURL(processed.thumb)
          : null;
        const previewUrl = isImage ? objectUrl : thumbObjectUrl;
        if (previewUrl) callbacks?.onPreview?.(previewUrl);

        const { data } = await createMediaUpload({
          // What the photo is FOR is decided at attach time (photo_link);
          // every gated upload is plain "media".
          variables: {
            input: {
              contentType: processed.contentType,
              kind: "media",
              sizeBytes: processed.blob.size,
              thumbSizeBytes: processed.thumb?.size ?? null,
              // Original filename labels documents/videos and names their
              // download. Images are re-encoded to JPEG and render from bytes,
              // so their original name would be misleading — omit it.
              fileName: processed.contentType.startsWith("image/")
                ? null
                : file.name,
            },
          },
        });
        if (signal?.aborted) {
          URL.revokeObjectURL(objectUrl);
          if (thumbObjectUrl) URL.revokeObjectURL(thumbObjectUrl);
          return null;
        }
        const uploaded = await uploadReservedMedia(
          data?.createMediaUpload,
          { blob: processed.blob, contentType: processed.contentType },
          processed.thumb
            ? { blob: processed.thumb, contentType: "image/jpeg" }
            : null,
          callbacks?.onProgress
            ? (fraction) => callbacks.onProgress!(Math.round(fraction * 100))
            : undefined,
          signal,
        );
        if (!uploaded.ok) {
          URL.revokeObjectURL(objectUrl);
          if (thumbObjectUrl) URL.revokeObjectURL(thumbObjectUrl);
          // A user cancel surfaces as an upload-stage failure too — stay silent
          // (the reserved row is reaped server-side); only toast real failures.
          if (!signal?.aborted) {
            toastManager.add({
              title:
                uploaded.stage === "reserve"
                  ? (uploaded.message ?? "Failed to start upload")
                  : "Failed to upload file",
              type: "error",
            });
          }
          return null;
        }
        registerMediaObjectUrl(
          uploaded.photoId,
          objectUrl,
          thumbObjectUrl ?? undefined,
        );
        return {
          photoId: uploaded.photoId,
          // Composer preview: image display blob, or the thumb for pdf/video.
          // Null for documents (no preview) — the card renders a file icon.
          objectUrl: previewUrl,
          contentType: processed.contentType,
          fileName: isImage ? null : file.name,
        };
      } catch {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        if (!signal?.aborted)
          toastManager.add({ title: "Failed to upload photo", type: "error" });
        return null;
      }
    },
    [createMediaUpload],
  );

  return { uploadNotePhoto };
}

/**
 * Permission-checked actions for non-image media. `openMedia` mints a presigned
 * URL and opens it in a new tab — PDFs view inline, office docs download (the
 * server sets Content-Disposition by type). Returns a stable callback.
 */
export function useMediaActions() {
  const { getAccessToken } = useAuth();
  const openMedia = useCallback(
    async (photoId: string) => {
      // Open the tab synchronously (pre-mint) so the popup isn't blocked, then
      // point it at the minted URL. NOTE: no "noopener" here — with it,
      // window.open returns null and we'd never get a handle to navigate. We
      // null the opener manually after navigating instead.
      const tab = window.open("about:blank", "_blank");
      try {
        const url = await resolveMediaPresignedUrl(photoId, { getAccessToken });
        if (tab) {
          tab.opener = null;
          tab.location.href = url;
        }
      } catch {
        tab?.close();
      }
    },
    [getAccessToken],
  );
  return { openMedia };
}

/**
 * Streamable video URL — a presigned GET fed straight to a <video> (range
 * requests, session-length TTL). Null until resolved. NOT the blob path, so
 * large videos don't download wholesale.
 */
export function useMediaStreamUrl(photoId: string | null): string | null {
  const { getAccessToken } = useAuth();
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!photoId) {
      setUrl(null);
      return;
    }
    let live = true;
    setUrl(null);
    resolveMediaPresignedUrl(photoId, { getAccessToken }).then(
      (resolved) => live && setUrl(resolved),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [photoId, getAccessToken]);
  return url;
}

/**
 * Authenticated display URL for a single note photo — resolves through the
 * shared object-URL cache. Returns null until resolved (or on failure).
 * Pass variant "thumb" for small tiles/strips; full-size views omit it.
 */
export function useMediaObjectUrl(
  photoId: string,
  variant: PhotoVariant = "full",
): string | null {
  const { getAccessToken } = useAuth();
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    // Callers pass "" to mean "no media here" (e.g. a document tile with no
    // thumbnail). Guard so we never enqueue an empty-id mint, which POSTs a
    // doomed content-url request and logs an error on every re-render.
    if (!photoId) {
      setUrl(null);
      return;
    }
    let live = true;
    setUrl(null);
    resolveMediaObjectUrl(photoId, { getAccessToken }, variant).then(
      (resolved) => live && setUrl(resolved),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [photoId, variant, getAccessToken]);

  return url;
}

/**
 * Note HTML with inline content-route image srcs swapped for authenticated
 * object URLs, plus a `resolving` flag. `html` is the raw input until
 * resolution lands (only relevant for notes that actually embed photos);
 * `resolving` is true while gated photos are still being fetched.
 *
 * Callers that mount an editor from this (the composer) should wait for
 * `resolving` to clear before mounting — swapping `html` after mount would
 * force a remount and discard in-progress edits.
 */
export function useResolvedMediaHtml(html: string): {
  html: string;
  resolving: boolean;
} {
  const { getAccessToken } = useAuth();
  const hasPhotos = useMemo(() => mediaIdsFromHtml(html).length > 0, [html]);
  const [resolved, setResolved] = useState(html);
  const [resolving, setResolving] = useState(hasPhotos);
  const [prevHtml, setPrevHtml] = useState(html);

  // Reset DURING render when the source html changes so this commit shows the
  // new raw html immediately — never the previous html's resolved markup,
  // which (when consumed by SanitizedHtml) would inject stale content with
  // mention slots mismatched against the new mention array for one frame.
  if (html !== prevHtml) {
    setPrevHtml(html);
    setResolved(html);
    setResolving(hasPhotos);
  }

  useEffect(() => {
    let live = true;
    if (!hasPhotos) {
      setResolving(false);
      return;
    }
    resolvePhotoHtml(html, { getAccessToken }).then(
      (next) => {
        if (!live) return;
        if (next !== html) setResolved(next);
        setResolving(false);
      },
      () => live && setResolving(false),
    );
    return () => {
      live = false;
    };
  }, [html, hasPhotos, getAccessToken]);

  return { html: resolved, resolving };
}
